/**
 * Reveals main content sections with a short fade and rise, once per element.
 *
 * Sections already in view when the script runs are left untouched, so the first
 * screen (and the LCP element) never waits on this module. Content is only hidden
 * by the `data-reveal` attribute this script sets, so markup stays visible when
 * the script is missing, blocked or disabled.
 */

/** Children revealed one after the other inside a section. */
const STAGGER_ITEMS =
  '.product-grid__item, .resource-list__item, .palmares__stat, .palmares__result, .palmares__step, [data-reveal-item]';

/** Upper bound for stagger steps so long lists don't lag behind the scroll. */
const MAX_STAGGER = 6;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * @param {Element} section
 * @returns {Element[]}
 */
function revealTargets(section) {
  const items = [...section.querySelectorAll(STAGGER_ITEMS)];
  if (items.length === 0) return [section];

  items.forEach((item, index) => {
    if (item instanceof HTMLElement) item.style.setProperty('--reveal-index', `${Math.min(index, MAX_STAGGER)}`);
  });
  return items;
}

function init() {
  if (reducedMotion.matches || !('IntersectionObserver' in window)) return;

  const main = document.getElementById('MainContent');
  if (!main) return;

  /** @type {Map<Element, Element[]>} */
  const pending = new Map();

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        for (const target of pending.get(entry.target) ?? []) {
          target.setAttribute('data-reveal', 'in');
          // Once shown, drop the attribute so hover transitions are not slowed by the reveal timing.
          target.addEventListener('transitionend', () => target.removeAttribute('data-reveal'), { once: true });
        }
        pending.delete(entry.target);
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -10% 0px' }
  );

  for (const section of main.querySelectorAll(':scope > .shopify-section')) {
    if (section.getBoundingClientRect().top < window.innerHeight) continue;

    const targets = revealTargets(section);
    for (const target of targets) target.setAttribute('data-reveal', '');
    pending.set(section, targets);
    observer.observe(section);
  }

  // Show everything at once if the visitor switches to reduced motion mid-visit.
  reducedMotion.addEventListener(
    'change',
    () => {
      observer.disconnect();
      for (const targets of pending.values()) for (const target of targets) target.setAttribute('data-reveal', 'in');
      pending.clear();
    },
    { once: true }
  );
}

init();
